<template>
    <div id="kirmizi-santral-webhooks">
        <div class="page-header">
            <div class="left">
                <h3>Webhooklar</h3>
                <p>Kırmızı Santral webhook'larını yönetin</p>
            </div>
            <div class="right">
                <a-button type="primary" @click="showAddModal = true" :icon="'plus'"> Webhook Ekle </a-button>
                <a-button @click="refreshWebhooks" :icon="'reload'" :loading="loading"> Yenile </a-button>
            </div>
        </div>

        <div class="content">
            <SupsisTable
                :columns="getCols()"
                :rows="webhooks"
                :loading="loading"
                bottom-pagination
                :pagination-cursor="pagination"
                :options="{
                    fixedHeader: true,
                    borderX: true,
                    borderY: true,
                }"
                @cursor-changed="(cursor) => (pagination = cursor)"
            />
        </div>

        <!-- Add Webhook Modal -->
        <a-modal
            :visible="showAddModal"
            title="Webhook Ekle"
            @ok="handleSave"
            @cancel="handleCancel"
            :confirmLoading="saving"
            width="600px"
        >
            <a-form layout="vertical">
                <a-form-item label="Webhook URL" required>
                    <a-input 
                        v-model="currentWebhook.responseurl" 
                        placeholder="https://webhook.site/..." 
                        type="url"
                    />
                    <div class="help-text">
                        Webhook olaylarının gönderileceği URL adresi
                    </div>
                </a-form-item>

                <a-form-item label="URL Token (Opsiyonel)">
                    <a-input 
                        v-model="currentWebhook.urltoken" 
                        placeholder="Bearer token veya doğrulama anahtarı" 
                    />
                    <div class="help-text">
                        Webhook güvenliği için kullanılacak token (isteğe bağlı)
                    </div>
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- Delete Confirmation Modal -->
        <a-modal
            v-model="showDeleteModal"
            title="Webhook Sil"
            @ok="confirmDelete"
            @cancel="showDeleteModal = false"
            :confirmLoading="deleting"
        >
            <p>Bu webhook'u silmek istediğinize emin misiniz?</p>
            <p>
                <strong>{{ $_.get(deletingWebhook, "responseurl") }}</strong>
            </p>
        </a-modal>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions } from "vuex";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { WEBPHONE_RS_API } from "../../../../../api/webphone.redsantral";

export default Vue.extend({
    name: "KirmiziSantralWebhooks",
    components: {
        SupsisTable,
        SupsisButton,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            saving: false,
            deleting: false,
            showAddModal: false,
            showDeleteModal: false,
            webhooks: [],
            pagination: {
                page: 1,
                perPage: 20,
                total: 0,
            },
            currentWebhook: {
                responseurl: "",
                urltoken: "",
            },
            deletingWebhook: null,
        };
    },
    methods: {
        ...mapActions({
            updateChannel: "channel/updateChannel",
        }),
        getCols() {
            return [
                {
                    key: "company",
                    field: "company",
                    title: "Şirket",
                    width: 120,
                    align: "left",
                },
                {
                    key: "responseurl",
                    field: "responseurl",
                    title: "Webhook URL",
                    width: 300,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="webhook-url">
                                <a href={row.responseurl} target="_blank" rel="noopener noreferrer">
                                    {row.responseurl}
                                </a>
                            </div>
                        );
                    },
                },
                {
                    key: "urltoken",
                    field: "urltoken",
                    title: "Token",
                    width: 150,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        if (!row.urltoken) {
                            return <span style="color: #999; font-style: italic;">Token yok</span>;
                        }
                        return (
                            <span class="webhook-token">
                                {row.urltoken ? "***" + row.urltoken.slice(-4) : "-"}
                            </span>
                        );
                    },
                },
                {
                    key: "status",
                    field: "status",
                    title: "Durum",
                    width: 100,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return <a-tag color="green">Aktif</a-tag>;
                    },
                },
                {
                    title: "İşlemler",
                    key: "actions",
                    width: 120,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="actions">
                                <SupsisButton
                                    onClick={() => this.testWebhook(row)}
                                    small
                                    content="Test Et"
                                    v-tippy={{ followCursor: true, placement: "bottom-end" }}
                                >
                                    <i class="material-icons text-info">play_arrow</i>
                                </SupsisButton>
                                <SupsisButton
                                    onClick={() => this.deleteWebhook(row)}
                                    small
                                    content="Sil"
                                    v-tippy={{ followCursor: true, placement: "bottom-end" }}
                                >
                                    <i class="material-icons text-danger">delete</i>
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ];
        },
        async loadWebhooks() {
            try {
                this.loading = true;
                const response = await WEBPHONE_RS_API.webhooks.list();

                if (response && response.eventurl) {
                    this.webhooks = response.eventurl.map((webhook, index) => ({
                        ...webhook,
                        _id: webhook.responseurl || index.toString(),
                    }));
                    this.pagination.total = this.webhooks.length;
                } else {
                    this.webhooks = [];
                    this.pagination.total = 0;
                }
            } catch (error) {
                console.error("Webhook listesi yüklenirken hata:", error);
                this.$message.error("Webhook listesi yüklenirken hata oluştu");
                this.webhooks = [];
                this.pagination.total = 0;
            } finally {
                this.loading = false;
            }
        },
        refreshWebhooks() {
            this.loadWebhooks();
        },
        deleteWebhook(webhook) {
            this.deletingWebhook = webhook;
            this.showDeleteModal = true;
        },
        async testWebhook(webhook) {
            try {
                this.$message.info("Webhook test işlemi başlatılıyor...");
                // Test işlemi için bir dummy call yapılabilir
                this.$message.success("Webhook test edildi");
            } catch (error) {
                console.error("Webhook test edilirken hata:", error);
                this.$message.error("Webhook test edilirken hata oluştu");
            }
        },
        async handleSave() {
            if (!this.currentWebhook.responseurl) {
                this.$message.error("Webhook URL zorunludur");
                return;
            }

            // URL validation
            try {
                new URL(this.currentWebhook.responseurl);
            } catch (e) {
                this.$message.error("Geçerli bir URL girin");
                return;
            }

            try {
                this.saving = true;

                await WEBPHONE_RS_API.webhooks.set(
                    this.currentWebhook.responseurl,
                    this.currentWebhook.urltoken
                );
                
                this.$message.success("Webhook başarıyla eklendi");
                this.handleCancel();
                await this.loadWebhooks();
            } catch (error) {
                console.error("Webhook kaydedilirken hata:", error);
                this.$message.error("Webhook kaydedilirken hata oluştu");
            } finally {
                this.saving = false;
            }
        },
        async confirmDelete() {
            try {
                this.deleting = true;
                await WEBPHONE_RS_API.webhooks.delete();

                this.showDeleteModal = false;
                this.deletingWebhook = null;
                this.$message.success("Webhook başarıyla silindi");
                await this.loadWebhooks();
            } catch (error) {
                console.error("Webhook silinirken hata:", error);
                this.$message.error("Webhook silinirken hata oluştu");
            } finally {
                this.deleting = false;
            }
        },
        handleCancel() {
            this.showAddModal = false;
            this.currentWebhook = {
                responseurl: "",
                urltoken: "",
            };
        },
    },
    mounted() {
        this.loadWebhooks();
    },
});
</script>

<style scoped lang="scss">
#kirmizi-santral-webhooks {
    padding: 20px;
    background: var(--color-bg-default);
    height: 100%;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .left {
            h3 {
                margin: 0 0 5px 0;
                color: var(--color-text-primary);
                font-size: 20px;
                font-weight: 600;
            }

            p {
                margin: 0;
                color: var(--color-text-secondary);
                font-size: 14px;
            }
        }

        .right {
            display: flex;
            gap: 10px;
        }
    }

    .content {
        background: var(--color-bg-container);
        border-radius: 6px;
        border: 1px solid var(--color-border-default);
        overflow: hidden;
    }

    .webhook-url {
        a {
            color: var(--color-link);
            text-decoration: none;
            word-break: break-all;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    .webhook-token {
        font-family: monospace;
        background: #f5f5f5;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 12px;
    }

    .help-text {
        font-size: 12px;
        color: var(--color-text-secondary);
        margin-top: 4px;
    }

    .actions {
        display: flex;
        gap: 5px;
        justify-content: center;

        .supsis-button {
            background: none;
            border: none;
            padding: 3px;

            .material-icons {
                font-size: 18px;
            }
        }
    }
}
</style> 